'use client';

import {
  Children,
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { useTranslations } from 'next-intl';
import useEmblaCarousel from 'embla-carousel-react';

/**
 * The canvas's slider arrow: a 40px dark scrim disc with a white glyph, inset
 * 14px from the edge. Deliberately not a themed surface — it sits on top of a
 * photograph, so it has to carry its own contrast rather than borrow the page's.
 * A translucent black works over both the light banners (Beretta on white) and
 * the dark ones (Nocpix on black) in this catalogue.
 */
const ARROW_CLASS =
  'absolute top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full ' +
  'bg-black/45 text-white transition-[background-color,opacity] hover:bg-black/70 disabled:opacity-0';

interface SliderCarouselProps {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  /** data-* attributes copied from the source node (keeps data-pb-style so the
      injected PageBuilder CSS — min-height, sizing — still applies). */
  dataAttrs?: Record<string, string>;
  showDots?: boolean;
  showArrows?: boolean;
  loop?: boolean;
  /** Advance slides automatically (PageBuilder data-autoplay). */
  autoplay?: boolean;
  /** Autoplay interval in ms (PageBuilder data-autoplay-speed). */
  autoplaySpeed?: number;
}

/**
 * Client carousel for the PageBuilder "slider" content type. Rendered by the
 * server `Slider` content-type component (see content-types/slider.tsx) so that
 * registration runs on the server while the interactive embla behaviour stays
 * client-only. Slides are passed as children (already server-rendered).
 */
export function SliderCarousel({
  children,
  className,
  style,
  dataAttrs,
  showDots = false,
  showArrows = false,
  loop = true,
  autoplay = false,
  autoplaySpeed = 5000,
}: SliderCarouselProps) {
  const t = useTranslations('common');
  const slides = Children.toArray(children).filter(Boolean);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: loop && slides.length > 1 });
  const [selected, setSelected] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const paused = useRef(false);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelected(emblaApi.selectedScrollSnap());
    setCanPrev(emblaApi.canScrollPrev());
    setCanNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect).on('reInit', onSelect);
  }, [emblaApi, onSelect]);

  const multiple = slides.length > 1;

  // Autoplay: advance on an interval, wrapping to the first slide at the end so
  // it keeps cycling even when infinite-loop drag is off. Pauses on hover.
  useEffect(() => {
    if (!emblaApi || !autoplay || !multiple) return;
    const delay = autoplaySpeed > 0 ? autoplaySpeed : 5000;
    const id = setInterval(() => {
      if (paused.current) return;
      if (emblaApi.canScrollNext()) emblaApi.scrollNext();
      else emblaApi.scrollTo(0);
    }, delay);
    return () => clearInterval(id);
  }, [emblaApi, autoplay, autoplaySpeed, multiple]);

  return (
    <div
      className={className}
      {...dataAttrs}
      style={{ position: 'relative', ...style }}
      onMouseEnter={autoplay ? () => (paused.current = true) : undefined}
      onMouseLeave={autoplay ? () => (paused.current = false) : undefined}
    >
      <div style={{ overflow: 'hidden' }} ref={emblaRef}>
        <div style={{ display: 'flex' }}>
          {slides.map((slide: ReactNode, i) => (
            <div key={i} style={{ flex: '0 0 100%', minWidth: 0 }}>
              {slide}
            </div>
          ))}
        </div>
      </div>

      {showArrows && multiple && (
        <>
          <button
            type="button"
            aria-label={t('previousSlide')}
            onClick={() => emblaApi?.scrollPrev()}
            disabled={!loop && !canPrev}
            className={ARROW_CLASS + ' left-[14px]'}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            type="button"
            aria-label={t('nextSlide')}
            onClick={() => emblaApi?.scrollNext()}
            disabled={!loop && !canNext}
            className={ARROW_CLASS + ' right-[14px]'}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      {showDots && multiple && (
        <div className="absolute inset-x-0 bottom-[14px] flex justify-center gap-2">
          {slides.map((_, i) => (
            <button
              type="button"
              key={i}
              aria-label={t('goToSlide', { number: i + 1 })}
              aria-current={selected === i}
              onClick={() => emblaApi?.scrollTo(i)}
              // The canvas's own pair: a solid accent for the current slide,
              // half-opacity white for the rest.
              className={`h-[9px] w-[9px] rounded-full transition-colors ${
                selected === i ? 'bg-accent-600' : 'bg-white/50'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
